import React, { useState, useEffect } from "react";
import { fetchJson, post } from "../../lib/Fetching";
import _ from "underscore";
import Button from "../Button";
import Link from "../Link";
import { observable } from "mobx";
import DilutionTestInput from "./DilutionTestInput";
import DilutionButton from "./DilutionButton";
import CurrentDilutionTestStatus from "./CurrentDilutionTestStatus";
import Tooltip from "../common/Tooltip";
import { validResult, dilutionTestParams } from "./DilutionUtils";
import { playSuccess, handleError, showErrorAlert } from "../../lib/StatusSounds";

export default function DilutionsInput({
  aliquot_id,
  onLoaded,
  onResultsChange,
  highlight = true,
  autoAddNonDetect = false,
}) {
  const [dilutionTests, setDilutionTests] = useState(null);
  const [currentDilutionTest, setCurrentDilutionTest] = useState(null);

  const highlightAliquotId = highlight ? aliquot_id : null;

  useEffect(
    () => {
      fetchDilutionTests();
    },
    [aliquot_id]
  );

  const refresh = () => {
    if (dilutionTests.filter((dt) => validResult(dt)).length > 0) {
      showErrorAlert("Cannot refresh results when results have been entered.");
      return;
    }
    fetchDilutionTests();
  };

  const handleOnSave = (json) => {
    if (json.errors) {
      showErrorAlert(json.errors);
    } else {
      playSuccess();
      fetchDilutionTests();
      onResultsChange && onResultsChange();
    }
  };

  const setAllAsNonDetect = () => {
    post("/sample_testing/plate_counting/set_all_as_non_detect", {
      sample_test_ids: dilutionTests.map((dt) => dt.sample_test_id),
    })
      .then(handleOnSave)
      .catch(handleError);
  };

  const setAllAsAboveMaxCount = () => {
    post("/sample_testing/plate_counting/set_all_as_above_max_count", {
      sample_test_ids: dilutionTests.map((dt) => dt.sample_test_id),
    })
      .then(handleOnSave)
      .catch(handleError);
  };

  const saveDilutionTests = (saveDilutionTests) => {
    post("/sample_testing/plate_counting/save_dilution_tests", {
      dilution_tests: dilutionTestParams(saveDilutionTests),
    })
      .then(handleOnSave)
      .catch(handleError);
  };

  const saveAllDilutionTests = () => {
    saveDilutionTests(dilutionTests.filter((dt) => validResult(dt)));
  };

  const saveDilutionTest = (dilutionTest) => {
    if (!validResult(dilutionTest)) {
      showErrorAlert("This is not a valid result. Cannot save!");
      return;
    }

    const otherEnteredResults = dilutionTests.filter(
      (dt) => dt !== dilutionTest && validResult(dt)
    );

    if (otherEnteredResults.length > 0) {
      showErrorAlert("Cannot save individual results when other results have been input!");
      return;
    }

    saveDilutionTests([dilutionTest]);
  };

  const fetchDilutionTests = () => {
    setDilutionTests(null);
    setCurrentDilutionTest(null);

    fetchJson("/sample_testing/related_dilution_tests.json", {
      aliquot_id,
    }).then((json) => {
      if (json.error) {
        alert(json.error);
        return;
      }

      const dTests = observable(json.map((s) => ({ ...s, input_count: "", result_type: 1 })));
      setDilutionTests(dTests);
      onLoaded && onLoaded(dTests);
    });
  };

  if (!dilutionTests) {
    return <i className="fa fa-spinner fa-spin" />;
  }

  const displayDilutionTests = _.sortBy(dilutionTests, (dt) =>
    dt.test_name.replace(/\(-\d+\)/, "").trim()
  );

  // Pick which test for the selected aliquot is focused.
  // Pick the first one in the visible list.
  const focusDilutionTest = displayDilutionTests.filter(
    (dt) => dt.aliquot_id === highlightAliquotId
  )[0];
  const hasFocus = (dilutionTest) => dilutionTest === focusDilutionTest;

  return (
    <div style={{ position: "relative" }}>
      {dilutionTests.filter((dt) => dt.result_value || dt.qualifier).length === 0 && (
        <div style={{ padding: 10, paddingBottom: 0 }}>
          <Button
            title="Save all as Non Detect"
            primary
            small
            onClick={setAllAsNonDetect}
            style={{ marginRight: 10 }}
          />
          <Button
            title="Save all as Above Max Count"
            primary
            small
            onClick={setAllAsAboveMaxCount}
            style={{ marginRight: 10 }}
          />
          <Tooltip id={"save-all-non-detect"}>
            <p>
              Clicking one of these buttons will immediately save the editable results below as
              Non Detect or Above Max Count.
            </p>
          </Tooltip>
        </div>
      )}

      <CurrentDilutionTestStatus dilutionTest={currentDilutionTest} />
      <table className="table table-hover dilution-input-table">
        <thead>
          <tr>
            <th>Label</th>
            <th>Test Name</th>
            <th>
              Result &nbsp;&nbsp;
              <Link onClick={refresh} className="pull-right">
                <i className="fa fa-refresh" />
                &nbsp; Refresh
              </Link>
            </th>
          </tr>
        </thead>
        <tbody>
          {displayDilutionTests.map((dt) => (
            <DilutionTestInput
              key={dt.sample_test_id}
              dilutionTest={dt}
              saveDilutionTest={saveDilutionTest}
              setCurrentDilutionTest={() => setCurrentDilutionTest(dt)}
              selected={hasFocus(dt)}
            />
          ))}
          <tr>
            <td colSpan={2} />
            <td style={{ paddingTop: 10, paddingBottom: 10, textAlign: "center" }}>
              <DilutionButton dilutionTests={dilutionTests} onClick={saveAllDilutionTests} />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
